<script setup>
import { ref, onBeforeUnmount } from "vue";
import { useCouponStore } from "@/stores/useCouponStore";

import { useDraggableModal } from "@/composables/useDraggableModal";

const couponStore = useCouponStore();
const emit = defineEmits(["close", "refresh"]);

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

// Ref ao <input type="datetime-local"> para checar validity.badInput,
// já que uma data/hora inválida não chega ao v-model (fica como "").
const expiresAtEl = ref(null);

const obj = ref({
  code: "",
  discountPercent: 20,
  expiresAt: "",
  isActive: true,
  eligibleRule: "ANYONE",
  applyToAllCycles: false,
  maxRedemptions: "",
});

const isSubmitting = ref(false);

// ====================================================================
// TOAST
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 2000);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

// ====================================================================
// NAV / CLOSE
// ====================================================================
const closeModal = () => {
  if (isSubmitting.value) return;
  emit("close");
};

// ====================================================================
// VALIDATION / PAYLOAD
// ====================================================================
function validateForm() {
  const code = obj.value.code?.trim();
  const discountPercent = Number(obj.value.discountPercent);
  const maxRedemptionsRaw = obj.value.maxRedemptions;

  if (!code) {
    showErrorToast("O código do cupom é obrigatório.");
    return false;
  }

  if (!Number.isInteger(discountPercent) || discountPercent < 1 || discountPercent > 100) {
    showErrorToast("O desconto deve ser um número inteiro entre 1 e 100.");
    return false;
  }

  // ----------------------------------------------------------------
  // Validação de data/hora (campo único datetime-local).
  // Quando o valor digitado não é parseável (data OU hora inválida),
  // o navegador deixa o .value vazio e marca validity.badInput = true.
  // Não há como distinguir qual parte falhou num único datetime-local,
  // então a mensagem deixa claro que pode ser data ou hora.
  // Por isso checamos badInput ANTES do "vazio".
  // ----------------------------------------------------------------
  const hasBadInput = !!expiresAtEl.value?.validity?.badInput;

  if (hasBadInput) {
    showErrorToast("Data ou hora de expiração inválida. Verifique os dois campos e digite valores válidos.");
    return false;
  }

  if (!obj.value.expiresAt) {
    showErrorToast("A data de expiração é obrigatória e deve incluir a hora.");
    return false;
  }

  // Salvaguarda: garante que o valor forma um instante válido.
  const dateObj = new Date(obj.value.expiresAt);
  if (Number.isNaN(dateObj.getTime())) {
    showErrorToast("Data ou hora de expiração inválida. Verifique os dois campos e digite valores válidos.");
    return false;
  }

  if (!obj.value.eligibleRule) {
    showErrorToast("A regra de elegibilidade é obrigatória.");
    return false;
  }

  if (maxRedemptionsRaw !== "" && maxRedemptionsRaw !== null && maxRedemptionsRaw !== undefined) {
    const maxRedemptions = Number(maxRedemptionsRaw);
    if (!Number.isInteger(maxRedemptions) || maxRedemptions < 1) {
      showErrorToast("O máximo de resgates deve ser um número inteiro maior que zero, ou vazio para ilimitado.");
      return false;
    }
  }

  return true;
}

function buildPayload() {
  const maxRedemptionsRaw = obj.value.maxRedemptions;

  return {
    code: obj.value.code.trim().toUpperCase(),
    discountPercent: Number(obj.value.discountPercent),
    expiresAt: obj.value.expiresAt ? new Date(obj.value.expiresAt).toISOString() : null,
    isActive: Boolean(obj.value.isActive),
    eligibleRule: obj.value.eligibleRule,
    applyToAllCycles: Boolean(obj.value.applyToAllCycles),
    maxRedemptions:
      maxRedemptionsRaw === "" || maxRedemptionsRaw === null || maxRedemptionsRaw === undefined
        ? null
        : Number(maxRedemptionsRaw),
  };
}

// ====================================================================
// SUBMIT
// ====================================================================
async function cadastrar() {
  if (isSubmitting.value) return;
  if (!validateForm()) return;

  try {
    isSubmitting.value = true;

    const payload = buildPayload();
    await couponStore.createCoupon(payload);

    showSuccessToast("Cupom criado com sucesso.");

    setTimeout(() => {
      isSubmitting.value = false;
      emit("refresh");
      emit("close");
    }, 1200);
  } catch (error) {
    let msg = "Erro ao cadastrar cupom.";

    if (error.response?.data?.errors) {
      msg = Object.values(error.response.data.errors).flat().join("\n");
    } else if (error.response?.data?.message) {
      msg = error.response.data.message;
    } else if (error.message) {
      msg = error.message;
    }

    showErrorToast(msg);
    isSubmitting.value = false;
  }
}

// ====================================================================
// KEYBOARD
// ====================================================================
const handleEscKey = (event) => {
  if (event.key === "Escape" && !isSubmitting.value) {
    emit("close");
  }
};

const handleEnterKey = (event) => {
  if (event.key !== "Enter") return;

  const el = event.target;
  const tag = (el?.tagName || "").toLowerCase();
  const isEditable = !!el?.isContentEditable;

  if (tag === "textarea" || isEditable) return;
  if (tag === "button" || tag === "a") return;

  if (!isSubmitting.value) {
    event.preventDefault();
    cadastrar();
  }
};

document.addEventListener("keydown", handleEscKey);
document.addEventListener("keydown", handleEnterKey);

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);
  document.removeEventListener("keydown", handleEnterKey);

  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});
</script>

<template>
  <div class="modal-backdrop" >
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="modal-title">Adicionar Novo Cupom</h3>
        <button
          @click="closeModal"
          type="button"
          class="modal-close-btn"
          :disabled="isSubmitting"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </button>
      </div>

      <form @submit.prevent="cadastrar" novalidate class="modal-form">
        <div class="modal-body">
          <div class="step-content">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="form-group-toggle toggle-card">
                <label for="isActive" class="form-label">Cupom ativo</label>
                <label class="toggle-switch">
                  <input v-model="obj.isActive" type="checkbox" id="isActive" />
                  <span class="slider"></span>
                </label>
              </div>

              <div class="form-group-toggle toggle-card">
                <label for="applyToAllCycles" class="form-label">Aplicar em todos os ciclos</label>
                <label class="toggle-switch">
                  <input
                    v-model="obj.applyToAllCycles"
                    type="checkbox"
                    id="applyToAllCycles"
                  />
                  <span class="slider"></span>
                </label>
              </div>
            </div>

            <div class="form-group">
              <label for="code" class="form-label">Código</label>
              <input
                v-model="obj.code"
                type="text"
                id="code"
                class="input-base code-input"
                placeholder="Ex.: LTR20"
                maxlength="50"
                required
                @focus="$event.target.select()"
              />
              <p class="field-hint">O código será salvo em caixa alta.</p>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="form-group">
                <label for="discountPercent" class="form-label">Percentual de desconto</label>
                <input
                  v-model.number="obj.discountPercent"
                  type="number"
                  id="discountPercent"
                  class="input-base"
                  min="1"
                  max="100"
                  step="1"
                  placeholder="Ex.: 20"
                  required
                />
              </div>

              <div class="form-group">
                <label for="expiresAt" class="form-label">Expira em</label>
                <input
                  ref="expiresAtEl"
                  v-model="obj.expiresAt"
                  type="datetime-local"
                  id="expiresAt"
                  class="input-base"
                  required
                />
                <p class="field-hint">É obrigatório preencher data e hora válidas.</p>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="form-group">
                <label for="eligibleRule" class="form-label">Regra de elegibilidade</label>
                <select v-model="obj.eligibleRule" id="eligibleRule" class="input-base">
                  <option value="ANYONE">Anyone</option>
                  <option value="TARGETED">Targeted</option>
                  <option value="FIRST_PAID">First Paid</option>
                </select>
              </div>

              <div class="form-group">
                <label for="maxRedemptions" class="form-label">Máximo de resgates</label>
                <input
                  v-model="obj.maxRedemptions"
                  type="number"
                  id="maxRedemptions"
                  class="input-base"
                  min="1"
                  step="1"
                  placeholder="Vazio = ilimitado"
                />
              </div>
            </div>

            <div class="rule-info-card">
              <div class="rule-info-title">
                <i class="fas fa-info-circle"></i>
                <span>Como interpretar as regras</span>
              </div>
              <ul>
                <li><strong>Anyone:</strong> qualquer usuário elegível pode usar o cupom.</li>
                <li><strong>Targeted:</strong> uso direcionado conforme vínculo em coupon_users.</li>
                <li><strong>First Paid:</strong> regra para primeiro pagamento pago.</li>
              </ul>
            </div>

            <div style="flex-grow: 1"></div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="isSubmitting"
            @click="closeModal"
          >
            Cancelar
          </button>

          <button
            type="submit"
            class="btn btn-success"
            :disabled="isSubmitting"
          >
            <svg
              v-if="!isSubmitting"
              class="mr-1 -ml-1 w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                clip-rule="evenodd"
              ></path>
            </svg>

            <svg
              v-else
              class="mr-2 -ml-1 w-5 h-5 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>

            <span>
              {{ isSubmitting ? "Adicionando..." : "Adicionar Cupom" }}
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>

.modal-panel {
  max-width: 34rem;
  height: 620px;
  max-height: calc(100vh - 20px - 2rem);
}

.modal-form {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.modal-footer {
  bottom: 0;
}

/* =========================
   CONTENT / FORM
========================= */
.step-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-height: 0;
}

.form-group-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.toggle-card {
  min-height: 50px;
  padding: 0.65rem 0.75rem;
  border: 1px solid var(--neutral-200);
  border-radius: 12px;
  background-color: var(--surface-2);
}

.field-hint {
  font-size: 0.76rem;
  color: var(--text-muted);
  margin-top: 0.1rem;
}

.input-base {
  height: 36px;
}

.code-input {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.rule-info-card {
  padding: 0.8rem 0.9rem;
  border-radius: 12px;
  border: 1px solid var(--neutral-200);
  background-color: var(--surface-2);
  color: var(--text-light);
}

.rule-info-title {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.84rem;
  font-weight: 700;
  color: var(--text-dark);
  margin-bottom: 0.45rem;
}

.rule-info-card ul {
  margin: 0;
  padding-left: 1.1rem;
  font-size: 0.8rem;
  line-height: 1.45;
}

/* =========================
   TOGGLE
========================= */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 38px;
  height: 22px;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: var(--neutral-200);
  transition: 0.25s;
  border-radius: 999px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 3px;
  top: 3px;
  background-color: var(--surface);
  transition: 0.25s;
  border-radius: 50%;
}

.toggle-switch input:checked + .slider {
  background-color: var(--success);
}

.toggle-switch input:checked + .slider:before {
  transform: translateX(16px);
}

</style>